/**
 * What the design system page sends to the server, built in one place for Save and Reset.
 *
 * The form holds more than the API takes: `activePlatform` (the open tab) and `mobileOs` (the
 * form's single value for the server's `{ ios, android }` pair) are form-only. The endpoint
 * rejects unknown fields (`forbidNonWhitelisted`), so a payload built by spreading the form state
 * fails with 400. Every payload goes through `designSystemPayload` for that reason.
 */

import type { DesignSystem, MobileOs } from './components/DesignSystemPanel';
import { elevationToShadows } from './designSystemConstants';

/** The server's shape for the phone OS: one flag is true, or neither when not chosen. */
export type MobileUxPatterns = { ios?: boolean; android?: boolean };

/**
 * The OS the page shows before the founder picks one. It is Android because that is what a phone
 * prototype is built as when nothing is chosen, so the page and the build agree.
 */
export const DEFAULT_MOBILE_OS: MobileOs = 'android';

/** The founder's saved choice, or undefined when the server has none (or an unclear one). */
export function mobileOsFromServer(
  patterns: MobileUxPatterns | null | undefined
): MobileOs | undefined {
  const ios = patterns?.ios === true;
  const android = patterns?.android === true;
  if (ios === android) return undefined;
  return ios ? 'ios' : 'android';
}

/**
 * The server's pair for a choice. With no choice nothing is sent, so saving a colour never turns
 * a startup's phone prototypes into one OS or the other.
 */
export function mobileUxPatternsFor(os: MobileOs | undefined): MobileUxPatterns | undefined {
  if (!os) return undefined;
  return { ios: os === 'ios', android: os === 'android' };
}

/** The fields of the design system row itself, as the API takes them. */
function designSystemFields(designSystem: DesignSystem) {
  return {
    primaryColor: designSystem.primaryColor,
    primaryForeground: designSystem.primaryForeground,
    secondaryColor: designSystem.secondaryColor,
    secondaryForeground: designSystem.secondaryForeground,
    fontFamily: designSystem.fontFamily,
    headingFontFamily: designSystem.headingFontFamily,
    theme: designSystem.theme,
    appBg: designSystem.appBg,
    surfaceColor: designSystem.surfaceColor,
    borderColor: designSystem.borderColor,
    mutedTextColor: designSystem.mutedTextColor,
    successColor: designSystem.successColor,
    warningColor: designSystem.warningColor,
    errorColor: designSystem.errorColor,
    infoColor: designSystem.infoColor,
    borderRadius: designSystem.borderRadius,
    // `shadows` is derived from the elevation style (the control the page
    // exposes) so the persisted DTO never ships a stale `none` shadow.
    shadows: elevationToShadows(designSystem.elevationStyle),
    density: designSystem.density,
    elevationStyle: designSystem.elevationStyle,
    buttonStyle: designSystem.buttonStyle,
    iconStyle: designSystem.iconStyle,
    inputStyle: designSystem.inputStyle,
    // Web-app UX patterns.
    uxPatterns: designSystem.uxPatterns ?? undefined,
    // Website (marketing-site) UX patterns.
    websiteUxPatterns: designSystem.websiteUxPatterns ?? undefined,
  };
}

/** The Save payload: the row's fields, the phone OS when chosen, and the project's own two. */
export function designSystemPayload(designSystem: DesignSystem) {
  return {
    ...designSystemFields(designSystem),
    mobileUxPatterns: mobileUxPatternsFor(designSystem.mobileOs),
    // Product type & language: the DS endpoint writes these through to the
    // project row, where the prototyper reads them (mode / language / direction).
    projectType: designSystem.projectType || undefined,
    language: designSystem.language || undefined,
  };
}

/**
 * The Reset payload: the defaults for the row's fields and "no OS chosen". Product type and
 * language belong to the project and are left out, so a reset never changes them.
 */
export function resetDesignSystemPayload(defaults: DesignSystem) {
  return {
    ...designSystemFields(defaults),
    // Sent as "neither", not left out: leaving it out would keep the old choice on the server
    // while the page shows the default.
    mobileUxPatterns: { ios: false, android: false },
  };
}
